import Fuse from 'fuse.js'
import { CategoryService } from './CategoryService'

export class SearchService {
  constructor(episodes) {
    this.episodes = episodes
    this.setupFuseIndex()
  }

  setupFuseIndex() {
    // Configuration de Fuse.js pour la recherche floue
    const fuseOptions = {
      includeScore: true,
      threshold: 0.3,
      keys: [
        { name: 'title', weight: 2 },
        { name: 'summary', weight: 1.5 },
        { name: 'guests', weight: 2 },
        { name: 'participants', weight: 2 },
        { name: 'themes', weight: 1.5 },
        { name: 'introNews.content', weight: 1 },
        { name: 'games.content', weight: 1.5 },
        { name: 'notableMoments.content', weight: 1.2 },
        { name: 'annoyance_stories.content', weight: 1 },
        { name: 'farfie_moments.content', weight: 1 }
      ]
    }

    this.fuse = new Fuse(this.episodes, fuseOptions)
    
    // Index secondaire pour la recherche dans le contenu détaillé
    this.setupDetailedIndex()
  }

  setupDetailedIndex() {
    // Créer un index détaillé pour rechercher dans tous les éléments de contenu
    const detailedItems = []
    
    this.episodes.forEach(episode => {
      // Actualités
      episode.introNews.forEach(news => {
        detailedItems.push({
          type: 'news',
          episodeId: episode.id,
          episodeTitle: episode.title,
          content: news.content,
          timestamp: news.timestamp,
          searchableText: `${episode.title} ${news.content}`
        })
      })

      // Jeux
      episode.games.forEach(game => {
        detailedItems.push({
          type: 'game',
          episodeId: episode.id,
          episodeTitle: episode.title,
          content: game.content,
          timestamp: game.timestamp,
          searchableText: `${episode.title} ${game.content}`
        })
      })

      // Recommandations culturelles
      episode.cultureRecommendations.forEach(rec => {
        const content = rec.type && rec.title ? `${rec.type} - ${rec.title}` : (rec.content || String(rec))
        detailedItems.push({
          type: 'culture',
          episodeId: episode.id,
          episodeTitle: episode.title,
          content: content,
          timestamp: rec.timestamp,
          searchableText: `${episode.title} ${content}`
        })
      })

      // Sections spécifiques à Podchiasse
      if (episode.annoyance_stories) {
        episode.annoyance_stories.forEach(story => {
          detailedItems.push({
            type: 'annoyance',
            episodeId: episode.id,
            episodeTitle: episode.title,
            content: story.content,
            timestamp: story.timestamp,
            searchableText: `${episode.title} ${story.content}`
          })
        })
      }

      if (episode.farfie_moments) {
        episode.farfie_moments.forEach(moment => {
          detailedItems.push({
            type: 'farfie',
            episodeId: episode.id,
            episodeTitle: episode.title,
            content: moment.content,
            timestamp: moment.timestamp,
            searchableText: `${episode.title} ${moment.content}`
          })
        })
      }

      // Moments notables
      episode.notableMoments.forEach(moment => {
        detailedItems.push({
          type: 'moment',
          episodeId: episode.id,
          episodeTitle: episode.title,
          content: moment.content,
          timestamp: moment.timestamp,
          searchableText: `${episode.title} ${moment.content}`
        })
      })
    })

    const detailedFuseOptions = {
      includeScore: true,
      threshold: 0.4,
      keys: ['searchableText', 'content']
    }

    this.detailedFuse = new Fuse(detailedItems, detailedFuseOptions)
  }

  // Recherche générale dans les épisodes
  searchEpisodes(query) {
    if (!query || query.trim().length < 2) {
      return this.episodes
    }

    const results = this.fuse.search(query.trim())
    return results.map(result => ({
      ...result.item,
      score: result.score
    }))
  }

  // Recherche détaillée dans le contenu
  searchContent(query) {
    if (!query || query.trim().length < 2) {
      return []
    }

    const results = this.detailedFuse.search(query.trim())
    return results.map(result => ({
      ...result.item,
      score: result.score
    }))
  }

  // Filtrer par invités
  filterByGuests(guestNames) {
    if (!guestNames || guestNames.length === 0) {
      return this.episodes
    }

    return this.episodes.filter(episode =>
      guestNames.some(guest =>
        episode.guests.some(episodeGuest =>
          episodeGuest.toLowerCase().includes(guest.toLowerCase())
        )
      )
    )
  }

  // Filtrer par jeux
  filterByGames(gameNames) {
    if (!gameNames || gameNames.length === 0) {
      return this.episodes
    }

    return this.episodes.filter(episode =>
      gameNames.some(game =>
        episode.games.some(episodeGame =>
          episodeGame.content.toLowerCase().includes(game.toLowerCase())
        )
      )
    )
  }

  // Filtrer par thèmes
  filterByThemes(themeNames) {
    if (!themeNames || themeNames.length === 0) {
      return this.episodes
    }

    return this.episodes.filter(episode =>
      themeNames.some(theme =>
        episode.themes.some(episodeTheme =>
          episodeTheme.toLowerCase().includes(theme.toLowerCase())
        )
      )
    )
  }

  // Obtenir tous les invités uniques
  getAllGuests() {
    const guests = new Set()
    this.episodes.forEach(episode => {
      episode.guests.forEach(guest => guests.add(guest))
    })
    return Array.from(guests).sort()
  }

  // Obtenir tous les jeux uniques
  getAllGames() {
    const games = new Set()
    this.episodes.forEach(episode => {
      episode.games.forEach(game => {
        // Extraire le nom du jeu de la description
        const gameName = this.extractGameName(game.content)
        if (gameName) games.add(gameName)
      })
    })
    return Array.from(games).sort()
  }

  // Obtenir tous les thèmes uniques
  getAllThemes() {
    const themes = new Set()
    this.episodes.forEach(episode => {
      episode.themes.forEach(theme => themes.add(theme))
    })
    return Array.from(themes).sort()
  }

  extractGameName(gameDescription) {
    // Essayer d'extraire le nom du jeu depuis la description
    // Chercher des patterns comme "Le jeu X" ou "X - description"
    const patterns = [
      /^([^-]+) -/,  // "Nom du jeu - description"
      /^Le (.*?)(?:\s|$)/, // "Le nom du jeu"
      /^(.*?) :/,    // "Nom du jeu : description"
      /^(.*?)(?:\s-\s|$)/ // "Nom du jeu - " ou "Nom du jeu"
    ]

    for (const pattern of patterns) {
      const match = gameDescription.match(pattern)
      if (match && match[1]) {
        return match[1].trim()
      }
    }

    // Si aucun pattern ne correspond, retourner les premiers mots
    const words = gameDescription.trim().split(' ')
    return words.slice(0, Math.min(3, words.length)).join(' ')
  }

  // Recherche combinée avec nouvelles capacités
  search(options = {}) {
    const {
      query = '',
      guests = [],
      games = [],
      themes = [],
      moods = [],
      contentTypes = [],
      culturalContentType = null,
      contentOnly = false
    } = options

    let results = this.episodes

    // Appliquer les filtres de catégories (moods et types de contenu)
    if (moods.length > 0 || contentTypes.length > 0) {
      results = CategoryService.filterByCategories(results, moods, contentTypes)
    }

    // Appliquer les filtres existants
    if (guests.length > 0) {
      results = results.filter(episode =>
        guests.some(guest =>
          (episode.guests || []).some(episodeGuest =>
            episodeGuest.toLowerCase().includes(guest.toLowerCase())
          ) ||
          (episode.participants || []).some(participant =>
            participant.toLowerCase().includes(guest.toLowerCase())
          )
        )
      )
    }

    if (games.length > 0) {
      results = results.filter(episode =>
        games.some(game =>
          (episode.games || []).some(episodeGame => {
            const gameContent = episodeGame.content || String(episodeGame)
            return gameContent.toLowerCase().includes(game.toLowerCase())
          })
        )
      )
    }

    if (themes.length > 0) {
      results = results.filter(episode =>
        themes.some(theme =>
          (episode.themes || []).some(episodeTheme =>
            episodeTheme.toLowerCase().includes(theme.toLowerCase())
          )
        )
      )
    }

    // Recherche de contenu culturel spécifique
    if (culturalContentType && query) {
      const culturalResults = CategoryService.searchCulturalContent(results, culturalContentType, query)
      return culturalResults.map(r => r.episode)
    }

    // Appliquer la recherche textuelle
    if (query.trim()) {
      if (contentOnly) {
        // Recherche dans le contenu détaillé uniquement
        const contentResults = this.searchContent(query)
        const episodeIds = new Set(contentResults.map(r => r.episodeId))
        results = results.filter(episode => episodeIds.has(episode.id))
      } else {
        // Recherche dans les épisodes
        const searchResults = this.searchEpisodes(query)
        const episodeIds = new Set(searchResults.map(r => r.id))
        results = results.filter(episode => episodeIds.has(episode.id))
      }
    }

    return results
  }

  // Méthode statique pour usage simple sans instanciation
  static searchEpisodes(episodes, query, filters = {}) {
    if (!episodes || episodes.length === 0) return []

    // Créer une instance temporaire pour la recherche
    const searchService = new SearchService(episodes)
    
    // Utiliser la méthode search avec les paramètres
    return searchService.search({
      query,
      guests: filters.guests || [],
      games: filters.games || [],
      themes: filters.themes || [],
      moods: filters.moods || [],
      contentTypes: filters.contentTypes || [],
      culturalContentType: filters.culturalContentType || null,
      contentOnly: filters.contentOnly || false
    })
  }

  // Méthodes statiques utilitaires
  static filterEpisodesByGuests(episodes, guestNames) {
    if (!guestNames || guestNames.length === 0) return episodes

    return episodes.filter(episode =>
      guestNames.some(guest =>
        episode.guests.some(episodeGuest =>
          episodeGuest.toLowerCase().includes(guest.toLowerCase())
        )
      )
    )
  }

  static filterEpisodesByGames(episodes, gameNames) {
    if (!gameNames || gameNames.length === 0) return episodes

    return episodes.filter(episode =>
      gameNames.some(game =>
        episode.games.some(episodeGame =>
          episodeGame.content.toLowerCase().includes(game.toLowerCase())
        )
      )
    )
  }

  static filterEpisodesByThemes(episodes, themeNames) {
    if (!themeNames || themeNames.length === 0) return episodes

    return episodes.filter(episode =>
      themeNames.some(theme =>
        episode.themes.some(episodeTheme =>
          episodeTheme.toLowerCase().includes(theme.toLowerCase())
        )
      )
    )
  }
} 